import { useRef, useEffect, useState } from 'react';
import { History, ChevronLeft, Loader2, MessageCircle, Trash2 } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { ChatInput, type AttachedFile } from './ChatInput';
import { LlmPresetSelector } from './LlmPresetSelector';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useChat } from '@/hooks/useChat';
import { useUIStore } from '@/stores/uiStore';
import { cn } from '@/lib/utils';
import type { TextContent, ImageContent } from '@/services/chatService';
import type { ChatSession } from '@/types';

interface ChatInterfaceProps {
  projectId?: string;
  className?: string;
  initialSessionId?: string | null;
  onSessionChange?: (sessionId: string | null) => void;
}

export function ChatInterface({
  projectId,
  className,
  initialSessionId,
  onSessionChange,
}: ChatInterfaceProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showSidebar, setShowSidebar] = useState(false);

  const {
    sessions,
    currentSession,
    messages,
    isStreaming,
    isLoading,
    thinkingContent,
    error,
    sendMessage,
    loadSessionById,
    deleteSessionById,
  } = useChat({ projectId, initialSessionId, onSessionChange });

  const { openDeleteConfirm } = useUIStore();

  const isGlobalChat = !projectId;

  // showTools state 제거됨 - GlobalChatTools 제거로 불필요

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    // Use direct scrollTop instead of scrollIntoView to prevent layout shifting
    // scrollIntoView can cause viewport displacement in complex flexbox layouts
    const scrollContainer = messagesEndRef.current?.closest('[data-radix-scroll-area-viewport]');
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async (content: string, files?: AttachedFile[]) => {
    if (files && files.length > 0) {
      const multimodalContent: (TextContent | ImageContent)[] = [];
      const textParts: string[] = [];

      if (content) {
        multimodalContent.push({ type: 'text', text: content });
      }

      for (const f of files) {
        try {
          if (f.file.type.startsWith('image/')) {
            const base64 = await readImageAsBase64(f.file);
            if (base64) {
              multimodalContent.push({
                type: 'image_url',
                image_url: { url: base64 },
              });
            }
          } else {
            const fileContent = await readFileContent(f.file);
            if (fileContent) {
              textParts.push(
                `--- 파일: ${f.file.name} (${formatFileSize(f.file.size)}) ---\n${fileContent}\n--- 파일 끝 ---`
              );
            } else {
              textParts.push(`[파일: ${f.file.name} - 내용을 읽을 수 없습니다]`);
            }
          }
        } catch {
          textParts.push(`[파일: ${f.file.name} - 읽기 오류]`);
        }
      }

      if (textParts.length > 0) {
        const textContent =
          multimodalContent.length === 0 && !content
            ? `다음 파일을 분석해주세요:\n\n${textParts.join('\n\n')}`
            : textParts.join('\n\n');
        multimodalContent.push({ type: 'text', text: textContent });
      }

      if (multimodalContent.length > 0 && !multimodalContent.some((p) => p.type === 'text')) {
        multimodalContent.unshift({ type: 'text', text: '이 이미지를 분석해주세요.' });
      }

      sendMessage(multimodalContent);
    } else {
      sendMessage(content);
    }
  };

  const readImageAsBase64 = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  };

  const readFileContent = async (file: File): Promise<string | null> => {
    if (
      file.type === 'text/plain' ||
      file.type === 'text/markdown' ||
      file.type === 'text/csv' ||
      file.type === 'application/json' ||
      file.name.endsWith('.md') ||
      file.name.endsWith('.txt') ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.json')
    ) {
      return await file.text();
    }

    if (file.type === 'application/pdf') {
      return `[PDF 파일입니다. PDF 내용을 분석하려면 각 페이지를 스크린샷으로 캡처하여 이미지로 첨부해주세요.]`;
    }

    return null;
  };

  return (
    <div className={cn('flex h-full', className)}>
      {/* Session Sidebar (Project chat only) */}
      {!isGlobalChat && showSidebar && (
        <div className="w-64 flex-shrink-0 border-r bg-muted/30">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-sm font-medium">대화 기록</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSidebar(false)}
              className="h-6 w-6 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>
          <ScrollArea className="h-[calc(100%-41px)]">
            {isLoading ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                대화 기록이 없습니다
              </div>
            ) : (
              <div className="p-2">
                {sessions.map((session) => (
                  <SessionItem
                    key={session.id}
                    session={session}
                    isActive={currentSession?.id === session.id}
                    onClick={() => loadSessionById(session.id)}
                    onDelete={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      openDeleteConfirm({
                        title: '대화 기록 삭제',
                        message: '이 대화 기록을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.',
                        onConfirm: () => deleteSessionById(session.id),
                      });
                    }}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex flex-1 flex-col">
        {/* Header Actions */}
        <div className="flex items-center justify-between border-b px-4 py-2">
          <div className="flex items-center gap-2">
            {!isGlobalChat && !showSidebar && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSidebar(true)}
                className="h-8 px-2"
              >
                <History className="mr-1 h-4 w-4" />
                기록
              </Button>
            )}
            {isGlobalChat ? (
              <LlmPresetSelector />
            ) : (
              <h3 className="text-sm font-medium">
                {currentSession?.title || '채팅'}
              </h3>
            )}
            {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center">
              <div className="rounded-full bg-primary/10 p-4">
                <MessageCircle className="h-8 w-8 text-primary" />
              </div>
              <h3 className="mt-4 text-lg font-semibold">
                {isGlobalChat ? '안녕하세요! 무엇을 도와드릴까요?' : '대화를 시작하세요'}
              </h3>
              <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                {projectId
                  ? '이 프로젝트에 대해 질문하거나 작업에 대한 도움을 받으세요.'
                  : '프로젝트 관리, 작업 확인, 질문에 대한 답변을 도와드릴 수 있습니다.'}
              </p>
              <div className="mt-6 grid gap-2 text-sm">
                {isGlobalChat ? (
                  <>
                    <SuggestionButton onClick={() => handleSendMessage('프로젝트 상태 보여줘')}>
                      프로젝트 상태 보여줘
                    </SuggestionButton>
                    <SuggestionButton onClick={() => handleSendMessage('오늘의 작업은 뭐야?')}>
                      오늘의 작업은 뭐야?
                    </SuggestionButton>
                    <SuggestionButton onClick={() => handleSendMessage('새 프로젝트 만들어줘')}>
                      새 프로젝트 만들어줘
                    </SuggestionButton>
                  </>
                ) : (
                  <>
                    <SuggestionButton
                      onClick={() => handleSendMessage('이 프로젝트를 이해하도록 도와줘')}
                    >
                      이 프로젝트를 이해하도록 도와줘
                    </SuggestionButton>
                    <SuggestionButton onClick={() => handleSendMessage('다음 단계는 뭐야?')}>
                      다음 단계는 뭐야?
                    </SuggestionButton>
                    <SuggestionButton onClick={() => handleSendMessage('프로젝트 계획을 생성해줘')}>
                      프로젝트 계획을 생성해줘
                    </SuggestionButton>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="pb-4">
              {messages.map((message, index) => {
                const isLastAssistant = index === messages.length - 1 && message.role === 'assistant';
                return (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    isStreaming={isStreaming && isLastAssistant}
                    thinkingContent={isStreaming && isLastAssistant ? thinkingContent : undefined}
                    projectId={projectId}
                  />
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Error Message */}
        {error && (
          <div className="border-t bg-destructive/10 px-4 py-2">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Input */}
        <ChatInput
          onSend={handleSendMessage}
          isStreaming={isStreaming}
          enableFileUpload={true}
          placeholder={
            projectId
              ? '이 프로젝트에 대해 질문하거나 파일을 드롭하세요...'
              : '대화를 시작하거나 파일을 드롭하세요...'
          }
        />
      </div>
    </div>
  );
}

function SessionItem({
  session,
  isActive,
  onClick,
  onDelete,
}: {
  session: ChatSession;
  isActive: boolean;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return '어제';
    } else if (diffDays < 7) {
      return `${diffDays}일 전`;
    } else {
      return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div
      className={cn(
        'group relative w-full flex items-center rounded-md px-1 py-1 transition-colors',
        isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
      )}
    >
      <button
        onClick={onClick}
        className="flex-1 px-2 py-1 text-left min-w-0"
      >
        <div className="truncate text-sm font-medium">{session.title || '새 대화'}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{formatDate(session.updatedAt)}</div>
      </button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(e);
        }}
        className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function SuggestionButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="rounded-lg border bg-background px-4 py-2 text-left transition-colors hover:bg-muted"
    >
      {children}
    </button>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
